import type { ChatMessage, ChatRequest, ChatResponse } from "../types";
import type { LogDomain } from "@/domains/logging";

import { core } from "@tauri-apps/api";
import { useState, useCallback, useRef } from "react";

import { debugLog } from "@/domains/logging";

/**
 * Custom hook for Ollama chat functionality
 * Manages chat state and communication with Ollama backend
 *
 * @returns Object with chat state and functions for message management
 *
 */
export const useOllamaChat = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<null | string>(null);
  const [currentModel, setCurrentModel] = useState<string>("deepseek-r1:8b");
  const [retryCount, setRetryCount] = useState<number>(0);

  // Use ref to avoid dependency issues
  const messagesReference = useRef<ChatMessage[]>([]);
  messagesReference.current = messages;

  /**
   * Send a message to Ollama
   */
  const sendMessage = useCallback(
    async (content: string, model?: string) => {
      if (!content.trim()) {
        return;
      }

      const modelToUse = model ?? currentModel;

      debugLog(
        "info",
        "llm" as LogDomain,
        `useOllamaChat: Sending message with model: ${modelToUse}`,
      );

      // Add user message
      const userMessage: ChatMessage = {
        content: content.trim(),
        id: crypto.randomUUID(),
        model: modelToUse,
        role: "user",
        timestamp: Date.now(),
      };

      // Update messages state
      const updatedMessages = [...messagesReference.current, userMessage];
      setMessages(updatedMessages);
      setIsLoading(true);
      setError(null);

      try {
        // First check if Ollama is healthy
        debugLog(
          "info",
          "llm" as LogDomain,
          "useOllamaChat: Checking Ollama health before sending message...",
        );
        const isHealthy = await core.invoke<boolean>("ollama_health_check");

        if (!isHealthy) {
          throw new Error(
            "Ollama service is not available. Please make sure Ollama is running on localhost:11434",
          );
        }

        debugLog(
          "info",
          "llm" as LogDomain,
          "useOllamaChat: Ollama is healthy, checking available models...",
        );

        // Check if the model is available
        const availableModels = await core.invoke<string[]>("ollama_get_models");
        debugLog(
          "info",
          "llm" as LogDomain,
          `useOllamaChat: Available models: ${JSON.stringify(availableModels)}`,
        );

        if (!availableModels.includes(modelToUse)) {
          const errorMessage = `Model '${modelToUse}' is not available. Available models: ${availableModels.join(", ")}. Please install the model first using: ollama pull ${modelToUse}`;
          debugLog("error", "llm" as LogDomain, `useOllamaChat: ${errorMessage}`);
          throw new Error(errorMessage);
        }

        debugLog(
          "info",
          "llm" as LogDomain,
          `useOllamaChat: Model '${modelToUse}' is available, proceeding with chat request`,
        );

        const request: ChatRequest = {
          messages: updatedMessages,
          model: modelToUse,
        };

        debugLog(
          "info",
          "llm" as LogDomain,
          `useOllamaChat: Sending request to Tauri: ${JSON.stringify(request)}`,
        );

        const response = await core.invoke<ChatResponse>("ollama_chat", {
          request,
        });

        debugLog(
          "info",
          "llm" as LogDomain,
          `useOllamaChat: Received response: ${JSON.stringify(response)}`,
        );

        if (response.success) {
          setMessages((previous) => [...previous, response.message]);
          setRetryCount(0); // Reset retry count on success
          debugLog("info", "llm" as LogDomain, "useOllamaChat: Message sent successfully");
        } else {
          throw new Error(response.error ?? "Unknown error occurred");
        }
      } catch (error_) {
        let errorMessage = error_ instanceof Error ? error_.message : "Failed to send message";

        // Handle specific timeout errors
        if (errorMessage.includes("operation timed out")) {
          if (retryCount < 2) {
            debugLog(
              "info",
              "llm" as LogDomain,
              `useOllamaChat: Timeout occurred, retrying... (attempt ${String(retryCount + 1)}/3)`,
            );
            setRetryCount((previous) => previous + 1);
            // Wait a bit and retry
            setTimeout(() => {
              void sendMessage(content, model);
            }, 2000);
            return;
          } else {
            errorMessage =
              "Request timed out after multiple attempts. The model might be starting up or processing a large request. Please try again later.";
          }
        } else if (errorMessage.includes("Request failed")) {
          errorMessage =
            "Connection to Ollama failed. Please make sure Ollama is running and accessible.";
        }

        setError(errorMessage);
        debugLog("error", "llm" as LogDomain, `useOllamaChat: Chat error: ${errorMessage}`);
        debugLog(
          "error",
          "llm" as LogDomain,
          `useOllamaChat: Full error object: ${JSON.stringify(error_)}`,
        );
      } finally {
        setIsLoading(false);
      }
    },
    [currentModel, retryCount],
  );

  /**
   * Clear chat history
   */
  const clearMessages = useCallback(() => {
    setMessages([]);
    setError(null);
    debugLog("info", "llm" as LogDomain, "useOllamaChat: Chat history cleared");
  }, []);

  /**
   * Set current model
   */
  const setModel = useCallback((model: string) => {
    setCurrentModel(model);
    debugLog("info", "llm" as LogDomain, `useOllamaChat: Model changed to: ${model}`);
  }, []);

  /**
   * Retry last message
   */
  const retryLastMessage = useCallback(async () => {
    if (messagesReference.current.length === 0) {
      return;
    }

    const messagesArray: ChatMessage[] = [...messagesReference.current];
    // Reason: toReversed() type inference issue - using manual reverse to avoid type errors
    const reversedMessages: ChatMessage[] = [];
    for (let i = messagesArray.length - 1; i >= 0; i--) {
      const message = messagesArray[i];
      if (message) {
        reversedMessages.push(message);
      }
    }
    const lastUserMessage: ChatMessage | undefined = reversedMessages.find(
      (message: ChatMessage) => message.role === "user",
    );
    if (!lastUserMessage) {
      return;
    }

    // Remove the last assistant message if it exists
    setMessages((previous) => {
      const lastIndex = previous.length - 1;
      if (previous[lastIndex]?.role === "assistant") {
        return previous.slice(0, lastIndex);
      }
      return previous;
    });

    await sendMessage(lastUserMessage.content, lastUserMessage.model);
  }, [sendMessage]);

  /**
   * Check Ollama health
   */
  const checkHealth = useCallback(async () => {
    try {
      debugLog("info", "llm" as LogDomain, "useOllamaChat: Checking Ollama health...");
      const isHealthy = await core.invoke<boolean>("ollama_health_check");
      debugLog(
        "info",
        "llm" as LogDomain,
        `useOllamaChat: Ollama health status: ${String(isHealthy)}`,
      );
      return isHealthy;
    } catch (error_) {
      const errorMessage = error_ instanceof Error ? error_.message : "Health check failed";
      debugLog("error", "llm" as LogDomain, `useOllamaChat: Health check error: ${errorMessage}`);
      return false;
    }
  }, []);

  /**
   * Get available models
   */
  const getAvailableModels = useCallback(async () => {
    try {
      debugLog("info", "llm" as LogDomain, "useOllamaChat: Fetching available models...");
      const models = await core.invoke<string[]>("ollama_get_models");
      debugLog(
        "info",
        "llm" as LogDomain,
        `useOllamaChat: Available models: ${JSON.stringify(models)}`,
      );
      return models;
    } catch (error_) {
      const errorMessage = error_ instanceof Error ? error_.message : "Failed to get models";
      debugLog("error", "llm" as LogDomain, `useOllamaChat: Get models error: ${errorMessage}`);
      return [];
    }
  }, []);

  return {
    checkHealth,
    clearMessages,
    currentModel,
    error,
    getAvailableModels,
    isLoading,
    messages,
    retryLastMessage,
    sendMessage,
    setModel,
  };
};
